import {
  List,
  Datagrid,
  TextField,
  Show,
  SimpleShowLayout,
  Button,
  useRecordContext,
  useRefresh,
  useNotify,
  type RaRecord,
} from 'react-admin';
import { Box, Chip } from '@mui/material';

function StatusChip({ value }: { value: string }) {
  const colorMap: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
    Verified: 'success',
    Failed: 'error',
    ManualReview: 'warning',
    Pending: 'info',
  };
  return <Chip label={value} color={colorMap[value] ?? 'default'} size="small" />;
}

function ReviewActions() {
  const record = useRecordContext<RaRecord>();
  const refresh = useRefresh();
  const notify = useNotify();

  if (!record || record.status !== 'ManualReview') return null;

  const handleApprove = async () => {
    try {
      const token = localStorage.getItem('rosta_token');
      await fetch(`${window.__ROSTA_API_URL__ || 'http://localhost:3200/api/v1'}/identity-verification/${record.id}/approve`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ reviewedBy: 'admin' }),
      });
      notify('Verification approved', { type: 'success' });
      refresh();
    } catch {
      notify('Failed to approve', { type: 'error' });
    }
  };

  const handleReject = async () => {
    try {
      const token = localStorage.getItem('rosta_token');
      await fetch(`${window.__ROSTA_API_URL__ || 'http://localhost:3200/api/v1'}/identity-verification/${record.id}/reject`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ reviewedBy: 'admin' }),
      });
      notify('Verification rejected', { type: 'success' });
      refresh();
    } catch {
      notify('Failed to reject', { type: 'error' });
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
      <Button label="Approve" onClick={handleApprove} />
      <Button label="Reject" onClick={handleReject} />
    </Box>
  );
}

function StatusField() {
  const record = useRecordContext<RaRecord>();
  if (!record) return null;
  return <StatusChip value={record.status as string} />;
}

export function IdentityVerificationList() {
  return (
    <List>
      <Datagrid rowClick="show">
        <TextField source="fullName" label="Name" />
        <TextField source="provider" label="Provider" />
        <StatusField />
        <TextField source="subjectRef.id" label="Candidate" />
        <TextField source="metadata.createdAt" label="Submitted" />
      </Datagrid>
    </List>
  );
}

export function IdentityVerificationShow() {
  return (
    <Show>
      <SimpleShowLayout>
        <TextField source="fullName" label="Full Name" />
        <TextField source="provider" label="Provider" />
        <StatusField />
        <TextField source="result.matched" label="Matched" />
        <TextField source="result.evidence" label="Evidence" />
        <TextField source="result.reference" label="Reference" />
        <TextField source="result.checkedAt" label="Checked At" />
        <TextField source="subjectRef.id" label="Candidate Profile ID" />
        <TextField source="metadata.createdAt" label="Created At" />
        <TextField source="metadata.updatedAt" label="Updated At" />
        <ReviewActions />
      </SimpleShowLayout>
    </Show>
  );
}
